import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useEstimatedTradeCostComparisonWidgetConfigSlice } from 'widgets/estimatedTradeCost/widget-comparison/store/configslice';
import { EstimatedTradeCostOptimization } from 'widgets/estimatedTradeCost/widget/store/EstimatedTradeCostConfigState';
import classNames from 'classnames';
import {
    NumericValueEditor,
    TimeInput,
    Icon,
    MessageIcon,
} from '@instinet-analytics/semantic-ui-components';
import { Time } from '@instinet-analytics/core-lib';

import 'widgets/estimatedTradeCost/widget-comparison/components/settings/EstimatedTradeCostComparisonWidgetSettings.scss';
import { BenchmarkDropdown } from 'widgets/estimatedTradeCost/components/BenchmarkDropdown';
import { ProfileDropdown } from 'widgets/estimatedTradeCost/components/ProfileDropdown';
import reducer, {
    actions,
    EstimatedTradeCostComparisonWidgetSettingsState,
} from 'widgets/estimatedTradeCost/widget-comparison/components/settings/EstimatedTradeCostComparisonWidgetSettingsState';
import { OnSaveHandler } from 'widgets/WidgetToolbar/WidgetSettingsButton';
import {
    isBenchmarkMissing,
    isProfileMissing,
    isStrikeTimeMissing,
    isValid,
} from 'widgets/estimatedTradeCost/widget-comparison/components/settings/EstimatedTradeCostComparisonWidgetSettingsValidation';
import {
    getProfileOptions,
    StrikeBenchmark,
} from 'widgets/estimatedTradeCost/components/options';

const MaxAllowedOptimizations = 6;

export function EstimatedTradeCostComparisonWidgetSettings({
    handleOnSave,
}: {
    handleOnSave: (handler: OnSaveHandler) => void;
}) {
    const { config, dispatchConfigChanged } =
        useEstimatedTradeCostComparisonWidgetConfigSlice();

    const initialState: EstimatedTradeCostComparisonWidgetSettingsState = {
        config,
    };
    const [state, dispatch] = useReducer(reducer, initialState);
    const [saveAttempted, setSaveAttempted] = useState(false);
    const [duplicateIndexes, setDuplicateIndexes] = useState<number[]>([]);

    useEffect(() => {
        if (config.optimizations.length === 0) {
            dispatch(actions.addRow());
        }
    }, [config.optimizations]);

    const onSave: OnSaveHandler = useCallback(() => {
        const { isValid: valid, duplicateIndexes: nextDuplicateIndexes } =
            isValid(state);
        setSaveAttempted(true);
        setDuplicateIndexes(nextDuplicateIndexes);

        if (valid) {
            dispatchConfigChanged(state.config);
        }
        return { cancel: !valid };
    }, [state, setSaveAttempted, dispatchConfigChanged]);

    useEffect(() => {
        handleOnSave(onSave);
    }, [handleOnSave, onSave]);

    const {
        config: { optimizations },
    } = state;

    const onClickAdd = () => {
        dispatch(actions.addRow());
    };

    const onClickRemove = (index: number) => {
        dispatch(actions.removeRow({ index }));
        setDuplicateIndexes([]);
    };

    const onUpdateRow = (
        index: number,
        delta: Partial<EstimatedTradeCostOptimization>
    ) => {
        dispatch(actions.updateRow({ index, delta }));
        setDuplicateIndexes([]);
    };

    return (
        <div className={'EstimatedTradeCostComparisonWidgetSettings'}>
            <div className="simple-grid">
                <div className="grid-row grid-row-header">
                    <div className="opt-benchmark">Benchmark</div>
                    <div className="opt-profile">Profile</div>
                    <div className="opt-min-part-lit">Min Part Lit</div>
                    <div className="opt-max-part-lit">Max Part Lit</div>
                    <div className="opt-strike-time">Strike Time</div>
                    <div className="grid-col col-actions"></div>
                </div>
                {optimizations.map((o, i) => (
                    <OptimizationRow
                        row={o}
                        index={i}
                        key={i.toString()}
                        first={i === 0}
                        last={i === optimizations.length - 1}
                        isDuplicate={duplicateIndexes.indexOf(i) > -1}
                        saveAttempted={saveAttempted}
                        onClickRemove={() => onClickRemove(i)}
                        onUpdateRow={(delta) => onUpdateRow(i, delta)}
                    />
                ))}
                {optimizations.length < MaxAllowedOptimizations && (
                    <div className="grid-row add-row">
                        <div
                            className="opt-value link-text"
                            onClick={onClickAdd}
                        >
                            Add...
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function OptimizationRow({
    row,
    first,
    last,
    index,
    isDuplicate,
    onClickRemove,
    onUpdateRow,
    saveAttempted,
}: {
    first: boolean;
    last: boolean;
    index: number;
    saveAttempted: boolean;
    isDuplicate: boolean;
    row: EstimatedTradeCostOptimization;
    onClickRemove: () => void;
    onUpdateRow: (delta: Partial<EstimatedTradeCostOptimization>) => void;
}) {
    const { benchmark, profile, minPartLit, maxPartLit, strikeTime } = row;

    const cssClasses = classNames(
        'opt-row grid-row',
        first && 'first-row',
        last && 'last-row',
        saveAttempted && 'show-validation-errors',
        saveAttempted && isBenchmarkMissing(row) && 'benchmark-missing',
        saveAttempted && isProfileMissing(row) && 'profile-missing',
        saveAttempted && isStrikeTimeMissing(row) && 'strike-time-missing',
        isDuplicate && 'is-duplicate'
    );

    const onBenchmarkChanged = (nextBenchmark: string) => {
        let nextProfile = profile;
        const nextProfileOptions = getProfileOptions(nextBenchmark);
        if (!nextProfileOptions.find((o) => o.value === profile)) {
            nextProfile = nextProfileOptions[0].value;
        }

        onUpdateRow({ benchmark: nextBenchmark, profile: nextProfile });
    };
    const onProfileChanged = (nextProfile: string) => {
        onUpdateRow({ profile: nextProfile });
    };
    const onMinLitChanged = (nextValue: number) => {
        onUpdateRow({ minPartLit: nextValue });
    };
    const onMaxLitChanged = (nextValue: number) => {
        onUpdateRow({ maxPartLit: nextValue });
    };
    const onStrikeTimeChanged = (nextValue: Time | undefined) => {
        onUpdateRow({ strikeTime: nextValue });
    };

    return (
        <div className={cssClasses}>
            <div className="grid-col opt-benchmark">
                {isDuplicate && (
                    <MessageIcon title={`Duplicated optimization`} />
                )}
                <BenchmarkDropdown
                    benchmark={benchmark}
                    bottom={index > 2}
                    profile={profile}
                    onChanged={onBenchmarkChanged}
                />
                {saveAttempted && !benchmark && (
                    <MessageIcon title={`Please select an benchmark`} />
                )}
            </div>
            <div className="grid-col opt-profile">
                <ProfileDropdown
                    bottom={index === MaxAllowedOptimizations - 1}
                    benchmark={benchmark}
                    profile={profile}
                    onChanged={onProfileChanged}
                />
                {saveAttempted && !profile && (
                    <MessageIcon title={`Please select an profile`} />
                )}
            </div>
            <div className="grid-col opt-min-part-lit">
                <NumericValueEditor
                    value={minPartLit ?? 0}
                    min={0}
                    max={100}
                    onChange={onMinLitChanged}
                />
            </div>
            <div className="grid-col opt-max-part-lit">
                <NumericValueEditor
                    value={maxPartLit ?? 0}
                    min={0}
                    max={100}
                    onChange={onMaxLitChanged}
                />
            </div>
            <div className="grid-col opt-strike-time">
                {benchmark === StrikeBenchmark && (
                    <TimeInput
                        time={strikeTime}
                        onChange={onStrikeTimeChanged}
                        className="strike-time"
                        showColon={true}
                    />
                )}
                {benchmark !== StrikeBenchmark && (
                    <div className="strike-time not-appl">N/A</div>
                )}
                {saveAttempted && isStrikeTimeMissing(row) && (
                    <MessageIcon title={`Please enter a strike time`} />
                )}
            </div>
            <div className="grid-col col-actions">
                {index > 0 && (
                    <Icon
                        name="trash"
                        className="delete-button"
                        title={`Delete`}
                        onClick={onClickRemove}
                    />
                )}
            </div>
        </div>
    );
}
